import { useState } from "react";
import { Check, ShoppingBag } from "lucide-react";
import PedidoModal from "@/components/PedidoModal";
import EquipacionModal from "@/components/EquipacionModal";
import { PRODUCTS, formatPrice } from "@/data/products";
import useSEO, { BASE_URL, DEFAULT_OG_IMAGE } from "@/hooks/useSEO";

export default function Tienda() {
  const [selected, setSelected] = useState(null);
  const [equipacionOpen, setEquipacionOpen] = useState(false);
  useSEO({
    title: "Tienda oficial · Camisetas y packs · La Bombonera",
    description:
      "Camisetas oficiales, shorts y packs del C.D. La Bombonera de Barcelona. Pack jugador 160€, personalizable con número y nombre. Envío gratuito y garantía.",
    canonical: `${BASE_URL}/tienda`,
    image: DEFAULT_OG_IMAGE,
  });

  return (
    <main data-testid="tienda-page" className="pt-32 pb-24">
      <div className="max-w-[1400px] mx-auto px-6 md:px-10 lg:px-16">
        {/* Header */}
        <div className="grid grid-cols-1 lg:grid-cols-12 gap-10 items-end">
          <div className="lg:col-span-7">
            <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mb-6 flex items-center gap-3">
              <span className="h-px w-10 bg-[#FFB800]" />
              Tienda oficial
            </p>
            <h1 className="font-display text-5xl md:text-7xl lg:text-8xl font-extrabold uppercase tracking-tight leading-[0.9]">
              Vestí<br />
              <span className="italic font-light text-white/70">los colores.</span>
            </h1>
          </div>
          <div className="lg:col-span-5">
            <p className="text-white/65 text-lg leading-relaxed">
              Camisetas, shorts y packs oficiales del C.D. La Bombonera de
              Barcelona. Personalizables con número y nombre. Producción
              cuidada y materiales premium.
            </p>
          </div>
        </div>

        {/* Featured Pack */}
        <FeaturedPackCard
          product={PRODUCTS[0]}
          onBuy={() => setEquipacionOpen(true)}
        />

        {/* Rest of the catalog */}
        <p className="text-[11px] uppercase tracking-[0.3em] text-[#FFB800] mt-24 mb-8 flex items-center gap-3">
          <span className="h-px w-10 bg-[#FFB800]" />
          Catálogo
        </p>
        <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-6">
          {PRODUCTS.slice(1).map((p) => (
            <ProductCard key={p.slug} product={p} onBuy={() => setSelected(p)} />
          ))}
        </div>
      </div>

      <EquipacionModal
        open={equipacionOpen}
        onClose={() => setEquipacionOpen(false)}
      />

      <PedidoModal
        product={selected}
        open={!!selected}
        onClose={() => setSelected(null)}
      />
    </main>
  );
}

function FeaturedPackCard({ product, onBuy }) {
  return (
    <section
      data-testid={`product-card-${product.slug}`}
      className="mt-16 relative rounded-[2.5rem] border border-[#FFB800]/30 bg-gradient-to-br from-[#FFB800]/8 via-[#0A1121] to-[#030712] overflow-hidden"
    >
      <div className="absolute top-6 right-6 z-10 rounded-full bg-[#FFB800] text-[#030712] px-4 py-1.5 font-mono text-[10px] uppercase tracking-[0.25em] font-bold">
        ★ Más vendido
      </div>

      <div className="grid grid-cols-1 lg:grid-cols-12 gap-8 p-6 md:p-10 lg:p-14 items-center">
        {/* Images */}
        <div className="lg:col-span-7 relative">
          <div className="grid grid-cols-2 gap-4">
            <div className="aspect-[3/4] rounded-[1.75rem] overflow-hidden bg-[#0B1D3A]">
              <img
                src={product.images[0]}
                alt={product.name}
                className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-1000"
              />
            </div>
            <div className="aspect-[3/4] rounded-[1.75rem] overflow-hidden bg-[#0B1D3A] mt-10">
              <img
                src={product.images[1] || product.images[0]}
                alt={`${product.name} 2`}
                className="w-full h-full object-cover hover:scale-[1.04] transition-transform duration-1000"
              />
            </div>
          </div>
        </div>

        {/* Info */}
        <div className="lg:col-span-5">
          <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800]">
            {product.category}
          </span>
          <h2 className="font-display text-4xl md:text-5xl lg:text-6xl font-extrabold uppercase tracking-tight leading-[0.95] mt-3">
            {product.name}
          </h2>
          <p className="text-white/70 mt-4 text-lg leading-relaxed">
            {product.short}
          </p>

          {product.includes && (
            <ul className="mt-6 flex flex-col gap-2.5">
              {product.includes.map((it, i) => (
                <li
                  key={i}
                  className="flex items-center gap-3 text-white/85 text-[15px]"
                >
                  <span className="h-6 w-6 rounded-full grid place-items-center bg-[#FFB800]/15 border border-[#FFB800]/40 text-[#FFB800] flex-shrink-0">
                    <Check size={12} strokeWidth={3} />
                  </span>
                  {it}
                </li>
              ))}
            </ul>
          )}

          <div className="mt-8 flex items-baseline gap-4">
            <span className="font-display text-5xl md:text-6xl font-extrabold text-[#FFB800]">
              {formatPrice(product.price)}
            </span>
            {product.oldPrice && (
              <span className="text-white/40 line-through text-xl">
                {formatPrice(product.oldPrice)}
              </span>
            )}
          </div>

          <button
            type="button"
            onClick={onBuy}
            data-testid={`buy-${product.slug}`}
            className="btn-primary mt-8"
          >
            Hacer pedido
            <ShoppingBag size={14} />
          </button>
        </div>
      </div>
    </section>
  );
}

function ProductCard({ product, onBuy }) {
  return (
    <article
      data-testid={`product-card-${product.slug}`}
      className="group relative rounded-[2rem] overflow-hidden bg-[#0A1121] border border-white/8 hover:border-[#FFB800]/40 transition-all duration-500 flex flex-col"
    >
      <div className="relative aspect-[4/5] overflow-hidden bg-[#0B1D3A]">
        <img
          src={product.images[0]}
          alt={product.name}
          loading="lazy"
          className="w-full h-full object-cover group-hover:scale-105 transition-transform duration-700"
        />
        {product.oldPrice && (
          <span className="absolute top-4 left-4 rounded-full bg-[#FFB800] text-[#030712] px-3 py-1 font-mono text-[10px] uppercase tracking-[0.2em] font-bold">
            -{Math.round((1 - product.price / product.oldPrice) * 100)}%
          </span>
        )}
      </div>
      <div className="p-6 flex-1 flex flex-col">
        <span className="text-[10px] uppercase tracking-[0.3em] text-[#FFB800] mb-2">
          {product.category}
        </span>
        <h3 className="font-display text-xl md:text-2xl font-extrabold uppercase tracking-tight">
          {product.name}
        </h3>
        <p className="text-white/55 text-[14px] mt-2 leading-relaxed">
          {product.short}
        </p>

        <div className="mt-auto pt-6 flex items-end justify-between gap-3">
          <div className="flex items-baseline gap-2">
            <span className="font-display text-2xl font-extrabold text-white">
              {formatPrice(product.price)}
            </span>
            {product.oldPrice && (
              <span className="text-xs text-white/35 line-through">
                {formatPrice(product.oldPrice)}
              </span>
            )}
          </div>
          <button
            type="button"
            onClick={onBuy}
            data-testid={`buy-${product.slug}`}
            className="rounded-full bg-[#FFB800] hover:bg-white text-[#030712] px-4 py-2 text-[11px] uppercase tracking-[0.2em] font-bold flex items-center gap-2 transition-colors"
          >
            Comprar
            <ShoppingBag size={12} />
          </button>
        </div>
      </div>
    </article>
  );
}
